/**
 * Класс для управления динамическим полем select в приложении
 * @package DynamicFieldClass
 * @version 1.0
 * @author BOLOTOV I.P.
 * @since 17.08.2022
 * @copyright (c) Internet-Frigate {@link http://www.ifrigate.ru}
 */
import { Inject, Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { InterfaceAppServiceRegister } from '@app/app.interface';
import { FuiDestroyService } from '@services/destroy.service';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DynamicFieldClass } from '../../../core/classes/DynamicField.class';
import { ISelect } from '../interfaces/Select.interface';
import { SelectServiceLib } from './SelectServiceLib.service';
@Injectable()
/** Класс для управления динамическим полем select */
export abstract class Select extends DynamicFieldClass implements ISelect {
    /** Constructor */
    protected constructor(
        @Inject(FuiDestroyService) protected _destroy$: FuiDestroyService,
        public service: SelectServiceLib,
    ) {
        super(service);
    }

    /**
     * Обработчик разрушения компонента
     * @private
     */
    protected _onDestroy: Subject<void> = new Subject<void>();
    /** Ссылка на элемент */
    matSelect: MatSelect;
    /** Фильтр */
    filteredSelect?: ReplaySubject<InterfaceAppServiceRegister[]> = new ReplaySubject<InterfaceAppServiceRegister[]>(1);

    /**
     * Установка опций select
     * @param options - набор опций
     */
    setOptions(options: InterfaceAppServiceRegister[]): void {
        this.service.field.options = options.slice();
    }

    /**
     * Обработка имени опции селекта
     * @param option: InterfaceAppServiceRegister
     */
    getOptionName(option: InterfaceAppServiceRegister): string {
        if (this.service.field.optionName) {
            return option[this.service.field.optionName];
        }
        return option.name;
    }

    /**
     * Выбор всех значений
     * @param selectAllValue - флаг выбора всех значений
     */
    toggleSelectAll(selectAllValue: boolean): void {
        const control: AbstractControl = this.service.group.get(this.service.field.name);

        this.filteredSelect.pipe(take(1), takeUntil(this._onDestroy)).subscribe((val) => {
            if (selectAllValue) {
                const value = [];
                val.forEach((v) => {
                    value.push(v.id);
                });
                control.setValue(value);
            } else {
                control.setValue([]);
            }
            control.markAsDirty();
        });
    }

    /** Проверка проставления всех checkbox */
    toggleAllCheckboxChecked(): boolean {
        if (!this.service.field.multiple) {
            return false;
        }

        const value: any = this.service.group?.get(this.service.field.name)?.value;

        if (!(value === 'object' && value)) {
            return false;
        }

        return this.service.options.length && this.service.options.every((child) => value.indexOf(child.id) !== -1);
    }

    /** Проверка части отмеченных полей */
    toggleAllCheckboxIndeterminate(): boolean {
        if (!this.service.field.multiple) {
            return false;
        }

        const value: any = this.service.group?.get(this.service.field.name)?.value;

        if (!(typeof value === 'object' && value)) {
            return false;
        }

        return !this.toggleAllCheckboxChecked() && value?.length !== 0;
    }
}
